/**
 * RichBodyEditor (iter-53) — compact contentEditable rich editor used
 * inside the Manual Compose ChapterCard so authors get bold/italic/
 * underline/heading/list buttons and the paste normalizer from
 * iter-49/50. Emits changes as sanitized HTML through `onChange` so it
 * plugs into the existing chapter.content field.
 *
 * When the incoming value looks like plain text (no HTML tags), it is
 * lifted into paragraphs so the seed always renders correctly.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline, Strikethrough, Heading2, Heading3, List, ListOrdered, Link as LinkIcon, Quote, Undo2, Redo2, ImagePlus } from "lucide-react";
import { normalizePastedHtml } from "@/components/manual/pasteNormalizer";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ALLOWED_TAGS = [
  "p", "br", "span", "div",
  "strong", "b", "em", "i", "u", "s", "sub", "sup",
  "h2", "h3", "h4",
  "ul", "ol", "li",
  "a", "blockquote", "code", "pre", "hr", "img",
];
const ALLOWED_ATTRS = ["href", "title", "rel", "target", "class",
                        "src", "alt", "width", "height"];

function _looksLikeHtml(text) {
  return typeof text === "string"
      && /<(p|h[1-6]|ul|ol|div|strong|em|u|s|blockquote|br\s*\/?)\b/i.test(text);
}
function _textToParagraphs(text) {
  if (!text) return "<p></p>";
  return text.split(/\n{2,}/)
    .map((p) => `<p>${DOMPurify.sanitize(p, { ALLOWED_TAGS: [] }).replace(/\n/g, "<br/>")}</p>`)
    .join("");
}

export function RichBodyEditor({
  value, onChange, permissive = false, bookId,
  testId, minHeight = 220, placeholder = "Paste or type the chapter body…",
}) {
  const ref = useRef(null);
  const fileInputRef = useRef(null);
  const seededOnceRef = useRef(false);
  // iter-54: remember the last HTML we committed to the parent. When
  // the parent bounces that same value back through the `value` prop
  // we must NOT re-seed — that wipes the caret mid-typing.
  const lastCommittedRef = useRef(null);
  const [dirty, setDirty] = useState(false);
  const [uploading, setUploading] = useState(false);
  // iter-54: memoize the attribute list so its reference doesn't
  // change on every render (which would rebuild `seed` and trigger
  // React to re-invoke the ref callback, wiping the caret + input).
  const allowedAttrs = useMemo(
    () => (permissive ? [...ALLOWED_ATTRS, "style"] : ALLOWED_ATTRS),
    [permissive]);

  // Seed the editor once on mount, and again ONLY when the incoming
  // `value` changes AND the editor isn't focused. Never re-seed on
  // arbitrary re-renders — that resets the caret in the middle of a
  // keystroke.
  const seed = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const html = _looksLikeHtml(value) ? value : _textToParagraphs(value || "");
    el.innerHTML = DOMPurify.sanitize(html, {
      ALLOWED_TAGS, ALLOWED_ATTR: allowedAttrs,
    });
    setDirty(false);
  }, [value, allowedAttrs]);

  // Ref callback runs ONCE on mount to attach the ref. Do NOT trigger
  // seeding here — that's the job of the effect below.
  const attachRef = useCallback((el) => { ref.current = el; }, []);

  // First mount: seed once. Subsequent value changes only re-seed if
  // the editor isn't focused AND the incoming value differs from what's
  // already in the DOM. This diff guard is critical — the onBlur commit
  // pushes sanitized HTML back through `value`; without the guard the
  // effect would re-seed on every commit-then-refocus cycle and wipe
  // the caret.
  useEffect(() => {
    if (!ref.current) return;
    if (!seededOnceRef.current) {
      seed();
      seededOnceRef.current = true;
      lastCommittedRef.current = ref.current.innerHTML;
      return;
    }
    if (document.activeElement === ref.current) return;
    // If the parent is echoing back a value we just committed, skip.
    if (value === lastCommittedRef.current) return;
    const incoming = _looksLikeHtml(value) ? value : _textToParagraphs(value || "");
    const incomingClean = DOMPurify.sanitize(incoming, {
      ALLOWED_TAGS, ALLOWED_ATTR: allowedAttrs,
    });
    if (ref.current.innerHTML === incomingClean) return;
    seed();
  }, [value, seed, allowedAttrs]);

  const commit = () => {
    if (!ref.current) return;
    const raw = ref.current.innerHTML;
    const clean = DOMPurify.sanitize(raw, {
      ALLOWED_TAGS, ALLOWED_ATTR: allowedAttrs,
    });
    lastCommittedRef.current = clean;
    onChange?.(clean);
    setDirty(false);
  };

  const cmd = (command, val = null) => {
    ref.current?.focus();
    document.execCommand(command, false, val);
    setDirty(true);
  };
  const insertBlock = (tag) => cmd("formatBlock", tag);

  // iter-108 — Add-link dialog. Replaces the old `window.prompt` UX
  // with a two-field modal (URL + Display text) so authors can insert
  // user-friendly labelled links instead of raw URLs. Detects the
  // caret's current context on open:
  //   * If the caret is inside an existing <a>, seeds the dialog with
  //     its href + text so the same button doubles as "Edit link".
  //   * If the caret has a text selection, seeds Display text with
  //     the selected string (Insert wraps it as the link body).
  //   * Otherwise, both fields start empty and Insert inserts a fresh
  //     anchor at the caret.
  // Selection state is captured on open (before the modal steals focus)
  // and restored right before we mutate the DOM, so the anchor lands
  // where the author expected.
  const [linkDialog, setLinkDialog] = useState(null); // { url, text, savedRange, existingAnchor }
  const savedRangeRef = useRef(null);
  const _saveSelection = () => {
    const sel = window.getSelection?.();
    if (!sel || sel.rangeCount === 0) { savedRangeRef.current = null; return; }
    savedRangeRef.current = sel.getRangeAt(0).cloneRange();
  };
  const _restoreSelection = () => {
    const el = ref.current; if (!el) return;
    el.focus();
    if (!savedRangeRef.current) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(savedRangeRef.current);
  };
  const _findAncestorAnchor = () => {
    const sel = window.getSelection?.();
    if (!sel || sel.rangeCount === 0) return null;
    let node = sel.anchorNode;
    while (node && node !== ref.current) {
      if (node.nodeType === 1 && node.nodeName === "A") return node;
      node = node.parentNode;
    }
    return null;
  };
  const openLinkDialog = () => {
    // Focus the surface first — if the click stole focus from the
    // editor, `window.getSelection` may return a stale range.
    ref.current?.focus();
    _saveSelection();
    const anchor = _findAncestorAnchor();
    const sel = window.getSelection?.();
    const selectedText = sel && !sel.isCollapsed ? sel.toString() : "";
    setLinkDialog({
      url: anchor ? (anchor.getAttribute("href") || "") : "",
      text: anchor ? (anchor.textContent || "") : selectedText,
      existingAnchor: anchor || null,
    });
  };
  const closeLinkDialog = () => setLinkDialog(null);
  const submitLinkDialog = () => {
    if (!linkDialog) return;
    let url = (linkDialog.url || "").trim();
    const text = (linkDialog.text || "").trim();
    if (!url) { toast.error("Enter a URL."); return; }
    // Auto-prefix bare domains so "example.com" still resolves.
    if (!/^(https?:|mailto:|tel:)/i.test(url)) {
      if (/^[\w.-]+@[\w.-]+/.test(url)) url = `mailto:${url}`;
      else url = `https://${url}`;
    }
    if (!/^(https?:|mailto:|tel:)/i.test(url)) {
      toast.error("Only http, https, mailto or tel links are allowed.");
      return;
    }
    _restoreSelection();
    if (linkDialog.existingAnchor) {
      // Update existing anchor in-place — swap href + label without
      // disturbing surrounding text.
      linkDialog.existingAnchor.setAttribute("href", url);
      linkDialog.existingAnchor.setAttribute("rel", "noopener");
      linkDialog.existingAnchor.setAttribute("target", "_blank");
      if (text) linkDialog.existingAnchor.textContent = text;
    } else {
      const label = text || url;
      const safe = label
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const safeUrl = url.replace(/"/g, "&quot;");
      const html = `<a href="${safeUrl}" rel="noopener" target="_blank">${safe}</a>`;
      document.execCommand("insertHTML", false, html);
    }
    setDirty(true);
    commit();
    setLinkDialog(null);
  };
  const removeLinkFromDialog = () => {
    if (!linkDialog?.existingAnchor) { setLinkDialog(null); return; }
    _restoreSelection();
    document.execCommand("unlink");
    setDirty(true);
    commit();
    setLinkDialog(null);
  };

  const insertLink = () => openLinkDialog();
  const handlePaste = (e) => {
    const html = e.clipboardData?.getData("text/html");
    const plain = e.clipboardData?.getData("text/plain") || "";
    if (!html) return;
    e.preventDefault();
    const clean = normalizePastedHtml(html, {
      allowedTags: ALLOWED_TAGS, allowedAttrs: ALLOWED_ATTRS, permissive,
    });
    // iter-109 — if the normalizer stripped the clipboard HTML down to
    // nothing (over-aggressive paste from some AI tools / email
    // signatures), fall back to the plain-text payload instead of
    // silently dropping the paste. Previously `if (!clean) return;`
    // left the author staring at an empty paragraph wondering where
    // their content went.
    let payload = clean;
    if (!payload || !payload.trim()) {
      if (!plain.trim()) return;
      payload = plain
        .split(/\n{2,}/)
        .map((para) => {
          const inner = para
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\n/g, "<br/>");
          return `<p>${inner}</p>`;
        })
        .join("");
    }
    ref.current?.focus();
    document.execCommand("insertHTML", false, payload);
    setDirty(true);
  };

  // iter-54: Enter should create a new paragraph. Chrome's default
  // varies (sometimes <div>, sometimes <br>) — we force `<p>` via
  // `defaultParagraphSeparator` and manually split lists/headings so
  // paragraphs stay clean.  iter-95e — `defaultParagraphSeparator`
  // fires once on mount before the surface is ever focused, so in
  // recent Chrome / Firefox it silently no-ops and Enter starts
  // producing broken empty rows again (see user screencast). Guard
  // by intercepting Enter ourselves whenever the caret is not inside
  // a list, blockquote, or heading — the three places where the
  // browser default already does the right thing.
  useEffect(() => {
    try { document.execCommand("defaultParagraphSeparator", false, "p"); }
    catch { /* noop */ }
  }, []);
  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && !e.altKey) {
      const key = e.key.toLowerCase();
      if (key === "b") { e.preventDefault(); return cmd("bold"); }
      if (key === "i") { e.preventDefault(); return cmd("italic"); }
      if (key === "u") { e.preventDefault(); return cmd("underline"); }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      // If the caret is inside a list item, blockquote or heading,
      // let the browser handle Enter — its default produces the
      // correct new <li> / paragraph split.
      let node = window.getSelection?.().anchorNode;
      let inSpecial = false;
      while (node && node !== ref.current) {
        const tag = (node.nodeName || "").toLowerCase();
        if (["li", "ul", "ol", "blockquote", "h1", "h2", "h3", "h4", "h5", "h6"].includes(tag)) {
          inSpecial = true;
          break;
        }
        node = node.parentNode;
      }
      if (!inSpecial) {
        e.preventDefault();
        // iter-95f — build the new paragraph manually so the caret
        // lands *inside* the empty <p>. `execCommand("insertHTML")`
        // splits the current paragraph but leaves caret placement
        // browser-dependent — recent Chrome puts the caret outside
        // the contentEditable, which the user perceives as the
        // cursor "jumping down below the chapter".
        const sel = window.getSelection?.();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const p = document.createElement("p");
        p.appendChild(document.createElement("br"));
        range.insertNode(p);
        // Split existing paragraph if we inserted in the middle: if
        // the new <p>'s parent is another <p>, move it to be a
        // sibling instead so we don't produce nested paragraphs.
        if (p.parentNode && p.parentNode.nodeName === "P") {
          const outer = p.parentNode;
          outer.parentNode.insertBefore(p, outer.nextSibling);
        }
        // Move the caret to the beginning of the new empty paragraph.
        const newRange = document.createRange();
        newRange.setStart(p, 0);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
        setDirty(true);
      }
    }
  };

  // Image insert — book-level upload (no source_index required).
  const openImagePicker = () => {
    if (!bookId) return toast.error("Save the book first, then insert images.");
    fileInputRef.current?.click();
  };

  // iter-55: image resize — clicking an inline image opens a small
  // floating chip strip anchored above it with 25 / 50 / 75 / 100 %
  // width targets. Sets a percentage width on the <img> so the
  // WeasyPrint export honours the same size.
  const [imgAnchor, setImgAnchor] = useState(null); // {top,left,el}
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const onClick = (e) => {
      if (e.target.tagName !== "IMG") {
        setImgAnchor(null); return;
      }
      const r = e.target.getBoundingClientRect();
      const wrapR = el.getBoundingClientRect();
      setImgAnchor({
        top: r.top - wrapR.top - 26,
        left: r.left - wrapR.left,
        img: e.target,
      });
    };
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, []);
  const setImgWidth = (pct) => {
    if (!imgAnchor?.img) return;
    imgAnchor.img.setAttribute("width", `${pct}%`);
    imgAnchor.img.style.width = `${pct}%`;
    setDirty(true);
    commit();
    setImgAnchor(null);
  };
  const onImageChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !bookId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/books/${bookId}/inline-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      ref.current?.focus();
      const alt = (file.name || "image").replace(/\.[a-z0-9]+$/i, "");
      document.execCommand("insertHTML", false,
        `<figure class="inline-figure"><img src="${data.url}" alt="${alt}"/></figure><p></p>`);
      setDirty(true);
      toast.success("Image inserted");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  // Push changes to parent on blur (or debounced by dirty flag).
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const onBlur = () => { if (dirty) commit(); };
    el.addEventListener("blur", onBlur);
    return () => el.removeEventListener("blur", onBlur);
  }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const btn = "p-1.5 hover:bg-[color:var(--canvas)] text-[color:var(--text-muted)] hover:text-[color:var(--text)] border-r border-[color:var(--border)] last:border-r-0";
  return (
    <div className="border border-[color:var(--border)] focus-within:border-[color:var(--primary)] relative"
         data-testid={testId}>
      <div className="flex flex-wrap items-center bg-[color:var(--canvas)]/60 border-b border-[color:var(--border)]"
           data-testid={`${testId}-toolbar`}>
        <button type="button" onMouseDown={(e)=>{e.preventDefault(); cmd("bold");}} className={btn} title="Bold (Ctrl+B)" data-testid={`${testId}-bold`}><Bold className="w-3.5 h-3.5"/></button>
        <button type="button" onMouseDown={(e)=>{e.preventDefault(); cmd("italic");}} className={btn} title="Italic (Ctrl+I)" data-testid={`${testId}-italic`}><Italic className="w-3.5 h-3.5"/></button>
        <button type="button" onMouseDown={(e)=>{e.preventDefault(); cmd("underline");}} className={btn} title="Underline (Ctrl+U)" data-testid={`${testId}-underline`}><Underline className="w-3.5 h-3.5"/></button>
        <button type="button" onMouseDown={(e)=>{e.preventDefault(); cmd("strikeThrough");}} className={btn} title="Strikethrough" data-testid={`${testId}-strike`}><Strikethrough className="w-3.5 h-3.5"/></button>
        <button type="button" onMouseDown={(e)=>{e.preventDefault(); insertBlock("h2");}} className={btn} title="Heading 2" data-testid={`${testId}-h2`}><Heading2 className="w-3.5 h-3.5"/></button>
        <button type="button" onMouseDown={(e)=>{e.preventDefault(); insertBlock("h3");}} className={btn} title="Heading 3" data-testid={`${testId}-h3`}><Heading3 className="w-3.5 h-3.5"/></button>
        <button type="button" onMouseDown={(e)=>{e.preventDefault(); cmd("insertUnorderedList");}} className={btn} title="Bulleted list" data-testid={`${testId}-ul`}><List className="w-3.5 h-3.5"/></button>
        <button type="button" onMouseDown={(e)=>{e.preventDefault(); cmd("insertOrderedList");}} className={btn} title="Numbered list" data-testid={`${testId}-ol`}><ListOrdered className="w-3.5 h-3.5"/></button>
        <button type="button" onMouseDown={(e)=>{e.preventDefault(); insertBlock("blockquote");}} className={btn} title="Blockquote" data-testid={`${testId}-quote`}><Quote className="w-3.5 h-3.5"/></button>
        <button type="button" onMouseDown={(e)=>{e.preventDefault(); insertLink();}} className={btn} title="Insert link" data-testid={`${testId}-link`}><LinkIcon className="w-3.5 h-3.5"/></button>
        <button type="button" onMouseDown={(e)=>{e.preventDefault(); openImagePicker();}} disabled={uploading} className={btn} title="Insert image" data-testid={`${testId}-image`}><ImagePlus className="w-3.5 h-3.5"/></button>
        <button type="button" onMouseDown={(e)=>{e.preventDefault(); cmd("undo");}} className={btn} title="Undo" data-testid={`${testId}-undo`}><Undo2 className="w-3.5 h-3.5"/></button>
        <button type="button" onMouseDown={(e)=>{e.preventDefault(); cmd("redo");}} className={btn} title="Redo" data-testid={`${testId}-redo`}><Redo2 className="w-3.5 h-3.5"/></button>
      </div>
      <input ref={fileInputRef} type="file" accept="image/*" onChange={onImageChosen}
             className="hidden" data-testid={`${testId}-image-input`}/>
      {imgAnchor && (
        <div className="absolute z-10 flex items-center gap-1 bg-white border border-[color:var(--primary)] px-1 py-0.5 shadow-sm text-[10px]"
             style={{ top: imgAnchor.top, left: imgAnchor.left }}
             data-testid={`${testId}-image-resize`}>
          <span className="text-[color:var(--text-muted)] uppercase tracking-widest mr-1">Width</span>
          {[25, 50, 75, 100].map((p) => (
            <button key={p} type="button" onMouseDown={(e)=>{e.preventDefault(); setImgWidth(p);}}
                    className="px-1.5 py-0.5 hover:bg-[color:var(--canvas)]"
                    data-testid={`${testId}-image-resize-${p}`}>
              {p}%
            </button>
          ))}
        </div>
      )}
      <div
        ref={attachRef}
        contentEditable
        suppressContentEditableWarning
        onInput={()=>setDirty(true)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onMouseDown={(e)=>{
          // iter-95d — reliably focus the contentEditable when the
          // author clicks anywhere on the surface, including empty
          // whitespace below the current content. Without this, clicks
          // in the padding area can end on the outer div and fail to
          // move the caret — the editor then swallows keystrokes and
          // paste events because `document.activeElement` is elsewhere.
          const el = ref.current;
          if (!el) return;
          if (document.activeElement === el) return;  // already focused
          // Only intercept when the click landed on the wrapper itself
          // (whitespace) — clicks on text nodes or child elements let
          // the native caret placement do its job.
          if (e.target !== el) return;
          e.preventDefault();
          el.focus();
          // Place the caret at the end of the content so typing appends
          // rather than replaces the whole node.
          try {
            const r = document.createRange();
            r.selectNodeContents(el);
            r.collapse(false);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(r);
          } catch { /* older browsers — focus alone is enough */ }
        }}
        className="wysiwyg-editor px-3 py-2 text-sm font-reading focus:outline-none cursor-text"
        style={{ minHeight }}
        data-placeholder={placeholder}
        data-testid={`${testId}-surface`}
      />
      {linkDialog && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={closeLinkDialog}
             data-testid={`${testId}-link-dialog-backdrop`}>
          <div className="bg-white max-w-sm w-full border border-[color:var(--border)]"
               onClick={(e) => e.stopPropagation()}
               data-testid={`${testId}-link-dialog`}>
            <div className="px-4 py-3 border-b border-[color:var(--border)] flex items-center justify-between">
              <p className="font-editorial text-base">
                {linkDialog.existingAnchor ? "Edit link" : "Add link"}
              </p>
              <button onClick={closeLinkDialog}
                      className="text-[color:var(--text-muted)] hover:text-black"
                      data-testid={`${testId}-link-dialog-x`}
                      aria-label="Close">×</button>
            </div>
            <div className="p-4 space-y-3">
              <label className="block">
                <span className="block text-[10px] uppercase tracking-widest mb-1">Display text</span>
                <input
                  type="text"
                  value={linkDialog.text}
                  onChange={(e) => setLinkDialog((d) => ({ ...d, text: e.target.value }))}
                  placeholder="e.g. Read the full article"
                  autoFocus
                  className="w-full border border-[color:var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--primary)]"
                  data-testid={`${testId}-link-dialog-text`}
                />
                <span className="block text-[10px] text-[color:var(--text-muted)] mt-1">
                  Leave blank to show the raw URL.
                </span>
              </label>
              <label className="block">
                <span className="block text-[10px] uppercase tracking-widest mb-1">URL</span>
                <input
                  type="url"
                  value={linkDialog.url}
                  onChange={(e) => setLinkDialog((d) => ({ ...d, url: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitLinkDialog(); } }}
                  placeholder="https://example.com"
                  className="w-full border border-[color:var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--primary)]"
                  data-testid={`${testId}-link-dialog-url`}
                />
                <span className="block text-[10px] text-[color:var(--text-muted)] mt-1">
                  Supports http, https, mailto: and tel:. Bare domains auto-prefix with https.
                </span>
              </label>
            </div>
            <div className="border-t border-[color:var(--border)] px-4 py-3 flex items-center justify-between gap-2">
              {linkDialog.existingAnchor ? (
                <button onClick={removeLinkFromDialog}
                        className="btn-ghost text-xs text-red-700 hover:text-red-800"
                        data-testid={`${testId}-link-dialog-remove`}>
                  Remove link
                </button>
              ) : <span/>}
              <div className="flex gap-2">
                <button onClick={closeLinkDialog}
                        className="btn-ghost text-xs"
                        data-testid={`${testId}-link-dialog-cancel`}>
                  Cancel
                </button>
                <button onClick={submitLinkDialog}
                        className="btn-primary text-xs"
                        data-testid={`${testId}-link-dialog-save`}>
                  {linkDialog.existingAnchor ? "Update link" : "Insert link"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
